import { Link, createFileRoute } from '@tanstack/react-router'
import { categoryApi } from '../../features/categories/services/categoryApi'
import { buildCategoryHierarchy } from '../../features/categories/utils/categoryTransformers'
import type { Category } from '../../features/categories/types/category'

export const Route = createFileRoute('/categories/')({
  loader: async () => {
    try {
      const response = await categoryApi.getAllCategories()
      // getAllCategories() returns Array<Category> — always flat backend items with parentId
      // buildCategoryHierarchy groups them into parent→children tree
      const categories = buildCategoryHierarchy(response)

      return { categories }
    } catch (error) {
      console.error('Failed to load categories:', error)
      return { categories: [] as Array<Category> }
    }
  },

  pendingComponent: () => (
    <div className="flex items-center justify-center py-20">
      <div className="text-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
        <p className="text-gray-600">Loading categories...</p>
      </div>
    </div>
  ),

  errorComponent: ({ error }) => (
    <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
      <h2 className="text-red-800 font-bold text-xl mb-2">
        Failed to Load Categories
      </h2>
      <p className="text-red-600 mb-4">{error.message}</p>
      <button
        onClick={() => window.location.reload()}
        className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
      >
        Retry
      </button>
    </div>
  ),

  component: CategoriesPage,
})

function CategoriesPage() {
  const data = Route.useLoaderData()
  const categories = data.categories

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Browse Categories</h1>
        <p className="text-gray-600">
          Explore {categories.length} main categories to find what you need.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {categories.map((category) => (
          <Link
            key={category.id}
            to="/categories/$"
            params={{ _splat: category.slug || String(category.id) }}
            className="bg-white rounded-lg shadow-md p-6 hover:shadow-xl transition-shadow"
          >
            <div className="mb-4 text-center">
              <div className="text-5xl mb-2">{category.icon || '📦'}</div>
              <h3 className="text-xl font-bold mb-1">{category.name}</h3>
              <p className="text-gray-600 text-sm">
                {category.count?.toLocaleString() || 0} listings
              </p>
            </div>
          </Link>
        ))}
      </div>
    </div>
  )
}
